import {
  Avatar,
  Card,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BackendService from 'BackendService';
import React, { useEffect, useState } from 'react';
import Contact from '../../models/Contact';
import FundOpportunity from '../../models/FundOpportunity';
import MessageToast from '../MessageToast';
import { Palette } from 'Palette';

interface Props {
  contact: Contact;
  isOpen: boolean;
  toggle: () => void;
  onComplete: () => void;
}

export default function AssignFundOpportunityModal({ contact, isOpen, toggle, onComplete }: Props) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<FundOpportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  }>();

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      setLoading(true);
      new BackendService()
        .searchFundOpportunitiesAvailableForContact(searchTerm, contact.id)
        .then((opportunities) => {
          setSearchResults(opportunities);
        })
        .catch((error) => {
          console.error('Error searching fund opportunities:', error);
          setMessage({
            type: 'error',
            text: 'Failed to search fund opportunities. Please try again.',
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, contact.id]);

  const assignOpportunity = (opportunityId: string) => {
    setLoading(true);
    setMessage(undefined);

    new BackendService()
      .createInvestor(contact.id, opportunityId)
      .then(() => {
        setMessage({
          type: 'success',
          text: 'Fund opportunity assigned successfully as candidate',
        });
        setTimeout(() => {
          onComplete();
          toggle();
          setSearchTerm('');
          setSearchResults([]);
        }, 1500);
      })
      .catch((error) => {
        console.error('Error assigning fund opportunity:', error);
        setMessage({
          type: 'error',
          text: 'Failed to assign fund opportunity. Please try again.',
        });
        setLoading(false);
      });
  };

  return (
    <>
      <Dialog open={isOpen} onClose={toggle} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Fund Opportunity to {contact.firstName} {contact.lastName}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Search Fund Opportunities"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            margin="normal"
            placeholder="Type to search for available opportunities..."
          />
          {loading && <CircularProgress size={24} />}
          {!loading && searchResults.length > 0 && (
            <Stack
              spacing={2}
              mt={2}
              sx={{
                backgroundColor: '#ffffff',
                color: '#666666',
                p: 2,
                borderRadius: 1,
                maxHeight: 320,
                overflow: 'auto',
              }}
            >
              {searchResults.map((opportunity) => (
                <Card
                  key={opportunity.id}
                  variant="outlined"
                  onClick={() => assignOpportunity(opportunity.id)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: '#ffffff',
                    color: '#666666',
                    borderColor: Palette.default.border,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <CardHeader
                    avatar={
                      <Avatar
                        sx={{
                          backgroundColor: Palette.default.iconCircle,
                          color: '#666666',
                        }}
                      >
                        {(opportunity.name || '?').charAt(0).toUpperCase()}
                      </Avatar>
                    }
                    title={opportunity.name}
                    subheader={
                      [
                        opportunity.summaryTitle,
                        opportunity.status,
                        opportunity.fundSize != null
                          ? `$${opportunity.fundSize.toLocaleString()}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || ' '
                    }
                    sx={{
                      color: '#666666',
                      '& .MuiCardHeader-title': { color: '#666666' },
                      '& .MuiCardHeader-subheader': {
                        color: '#888888',
                        opacity: 1,
                      },
                    }}
                  />
                </Card>
              ))}
            </Stack>
          )}
          {!loading && searchTerm.trim() && searchResults.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              No available fund opportunities found.
            </Typography>
          )}
          {!searchTerm.trim() && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              Start typing to search for fund opportunities to assign.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {message && (
        <MessageToast
          open={true}
          message={message.text}
          type={message.type}
          onClose={() => setMessage(undefined)}
        />
      )}
    </>
  );
}
